import React from "react";
import { motion } from "framer-motion";
import {
  Building2, Home, Landmark, Gavel, ClipboardCheck,
  HardHat, FileCheck2, FileSignature, Hammer, Lightbulb,
  ArrowUpRight,
} from "lucide-react";

const SERVICES = [
  { icon: Building2,      title: "Avalúos Comerciales",     desc: "Valuación de locales, oficinas y propiedades de uso comercial con metodología certificada." },
  { icon: Home,           title: "Avalúos Inmobiliarios",   desc: "Determinación del valor real de inmuebles residenciales y terrenos con respaldo técnico." },
  { icon: Landmark,       title: "Justipreciación de Rentas", desc: "Determinación técnica del valor de renta justo para inmuebles habitacionales, comerciales e industriales." },
  { icon: Gavel,          title: "Peritajes Técnicos",      desc: "Dictámenes periciales con validez legal ante autoridades, juzgados y dependencias oficiales." },
  { icon: ClipboardCheck, title: "Dictámenes Técnicos",     desc: "Evaluación técnica de condiciones estructurales, de habitabilidad o daños en inmuebles." },
  { icon: HardHat,        title: "Dirección Responsable de Obra (DRO)", desc: "Firma y supervisión responsable de proyectos de construcción ante el municipio de Puebla." },
  { icon: FileCheck2,     title: "Regularización de Inmuebles", desc: "Gestión integral para regularizar propiedades con situaciones irregulares ante el Registro Público." },
  { icon: FileSignature,  title: "Licencias y Trámites",    desc: "Asesoría y gestión de licencias de construcción, uso de suelo y permisos municipales." },
  { icon: Hammer,         title: "Supervisión de Obra",     desc: "Control técnico de calidad, avance y normativa durante la ejecución de obras de construcción." },
  { icon: Lightbulb,      title: "Consultoría Técnica",     desc: "Asesoría profesional en proyectos de inversión, desarrollo inmobiliario y resolución de conflictos técnicos." },
];

export default function Services() {
  return (
    <section
      id="servicios"
      data-testid="services-section"
      className="relative bg-brand-light py-24 lg:py-32 overflow-hidden"
    >
      <div aria-hidden className="absolute inset-0 bg-blueprint-grid bg-grid-40 opacity-50" />
      <div aria-hidden className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-white to-transparent" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3">
            <span className="h-px w-10 bg-brand-gold" />
            <span className="label-overline">Nuestros Servicios</span>
          </div>
          <h2 className="mt-5 font-playfair text-3xl sm:text-4xl lg:text-5xl text-brand-navy leading-[1.1] tracking-tight text-balance">
            Soluciones técnicas <span className="italic text-brand-gold">especializadas</span> para cada necesidad
          </h2>
          <p className="mt-5 font-inter text-lg text-brand-mute leading-relaxed max-w-2xl">
            Ofrecemos servicios integrales con precisión técnica y respaldo legal en toda la
            región de Puebla.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: (i % 3) * 0.08, ease: "easeOut" }}
              className="group relative bg-white border border-brand-navy/8 rounded-sm p-7 lg:p-8 shadow-[0_2px_18px_-12px_rgba(27,42,74,0.18)] hover:shadow-[0_30px_60px_-30px_rgba(27,42,74,0.35)] hover:-translate-y-1 transition-all duration-300 overflow-hidden"
              data-testid={`service-card-${i}`}
            >
              {/* Top gold bar */}
              <span className="absolute top-0 left-0 right-0 h-[3px] bg-brand-gold/80 group-hover:bg-brand-gold transition-colors" />
              {/* Hover navy overlay */}
              <span className="absolute inset-0 bg-brand-navy opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              <div className="relative">
                <div className="flex items-center justify-between">
                  <div className="flex h-14 w-14 items-center justify-center rounded-sm border border-brand-gold/40 bg-brand-light group-hover:bg-brand-navy/40 group-hover:border-brand-gold transition-colors">
                    <s.icon size={26} strokeWidth={1.5} className="text-brand-gold" />
                  </div>
                  <span className="font-playfair italic text-3xl text-brand-navy/10 group-hover:text-brand-gold/30 transition-colors">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>

                <h3 className="mt-6 font-playfair text-xl lg:text-[22px] leading-snug text-brand-navy group-hover:text-white transition-colors">
                  {s.title}
                </h3>
                <p className="mt-3 font-inter text-sm leading-relaxed text-brand-mute group-hover:text-white/75 transition-colors">
                  {s.desc}
                </p>

                <div className="mt-6 flex items-center gap-2 font-mont text-[10px] uppercase tracking-[0.22em] text-brand-gold-dark group-hover:text-brand-gold transition-colors">
                  Ver más
                  <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
